import React, {
  useState, useEffect, useReducer,
} from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  ComponentLevelNotification,
  Label,
} from '@americanexpress/dls-react';
import useDynamicValidations from '../../CustomHooks/useDynamicValidations';
import {
  parseHtml,
  convertFullToHalfWidth,
  formatUnFormatString,
} from '../../utils/util';
import { validateDlsInput } from '../../Validations/Validations';
import { getRequiredData } from '../../constants';
import { performOperations } from '../../Operations/Operations';
import { calculatorDlsInput } from '../Calculations/Calculation';

const getVal = require('lodash/get');

/**
 * returns DLS input component
 * @param {*} props
 */
/* eslint complexity: ["error", 48] -- need to increase complexity */
/* eslint-disable max-len -- need */
const DlsInput = (props) => {
  const {
    fieldLabel,
    fieldPlaceholder,
    fieldId,
    fieldValue,
    fieldValidation,
    filledFormData,
    memberInfo,
    selectKey,
    preFilledWith,
    isPreFilled,
    fetchUrl,
    textWidth,
    isVerifyCard,
    setFormSubmissionPayload,
    formSubmissionPayload,
    validations = [],
    dynamicValidations = [],
    dynamicValidationDeps = ['curPageValues'],
    setValidationErrors,
    validationErrors,
    setFocus,
    fieldRank,
    entityToEdit,
    idsToClear,
    curPageValues,
    setCurPageValues,
    operationsToPerform,
    dispatchIdsState,
    extractMapResponseValueByKey,
    comments,
    setComments,
    entityComments,
    entityCompositeKey,
    showId,
    dataObject,
    inputFormat,
    changePageValues,
    setChangePageValues,
    isChangePageValue = false,
    fieldDynamicRequired = null,
    journeyId,
    iformIntegrationUrls,
    loggerApiCall,
    isSelectivePrefill = false,
    getReviewDisabled,
    objectOfFunctions,
    prefillStyleConfig,
    setIsFormSubmission,
  } = props;

  const [data, setData] = useState(null);
  const [inValidError, setInvalidError] = useState(false);
  const [customInvalidMsg, setCustomInvalidMsg] = useState('');
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [status, setStatus] = useState('default');
  const [isValid, setIsValid] = useState(true);
  const [isUserEdited, setIsUserEdited] = useState(false);

  // Prefill style: only computed when prefillStyleConfig is provided; fields without it
  // skip this block entirely.
  // - matchAPIKey is the full dotted path from formSubmissionPayload, e.g.
  //   "arketeam_taxId.users.0.businessInformation.businessName" for a flat field, or
  //   "arketeam_taxId.users.0.businessInformation.legalRepresentatives.{index}.names.0.first"
  //   for a row-level field (use "{index}" placeholder; replaced at runtime with __rawApiIndex).
  // - sourcePath names the bucket holding this field's own current value (e.g.
  //   "formSubmissionPayload" or "entityToEdit", looked up via objectOfFunctions); fieldId is
  //   used as the key into that bucket.
  // - The style is only applied when both values exist and are equal and the user hasn't edited.
  let showPrefillStyle = false;
  if (prefillStyleConfig) {
    /* eslint-disable-next-line no-underscore-dangle -- internal marker set during API prefill */
    const rawApiIndex = entityToEdit?.__rawApiIndex ?? 0;
    const resolvedKey = prefillStyleConfig.matchAPIKey?.replace('{index}', rawApiIndex);
    const apiValue = getVal(formSubmissionPayload, resolvedKey);
    const currentValueSource = getVal(objectOfFunctions, prefillStyleConfig.sourcePath);
    const currentValue = getVal(currentValueSource, fieldId);
    const hasPrefillSource = apiValue !== undefined && apiValue !== null && apiValue !== ''
      && apiValue === currentValue;
    showPrefillStyle = hasPrefillSource && !isUserEdited;
  }
  const payloadFieldValue = filledFormData ? filledFormData[fieldId] : undefined;
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );
  const optionalLabel = fieldLabel
    && Object.prototype.hasOwnProperty.call(fieldLabel, 'optionalLabel')
    ? fieldLabel.optionalLabel
    : ' (Optional)';

  const performCustomValidations = (params) => {
    const { inputVal, validationsToPerform } = params;
    const invalidMsg = validateDlsInput(inputVal, validationsToPerform, {
      ...dataObject,
      isFieldRequired: fieldValidation.required,
    });
    if (invalidMsg) {
      dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
    } else {
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useDynamicValidations({
    data,
    dynamicValidations,
    performCustomValidations,
    dependencyArray:
      dynamicValidations.length > 0
        ? dynamicValidationDeps.reduce(
          (deps, key) => {
            deps.push(getVal(props, key));
            return deps;
          },
          [curPageValues[fieldId]]
        )
        : [],
  });

  useEffect(() => {
    if (fieldDynamicRequired && fieldValidation && !fieldValidation.required) {
      const mandatoryValidation = validations.find(
        (val) => val.type === 'mandatoryField'
      );
      if (mandatoryValidation) {
        performCustomValidations({
          inputVal: data,
          validationsToPerform: validations,
        });
      }
    }
  }, [fieldValidation.required]);

  /**
   * handle input value
   * @param {*} event
   */
  const autoFormatDateInput = (rawValue = '') => {
    const digitsOnly = String(rawValue).replace(/\D/g, '').slice(0, 8);
    if (digitsOnly.length <= 2) return digitsOnly;
    if (digitsOnly.length <= 4) {
      return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
  };

  const getDateCaretPosition = (rawValue = '', rawCaretPosition = 0) => {
    const digitsBeforeCaret = String(rawValue)
      .slice(0, rawCaretPosition)
      .replace(/\D/g, '').length;
    if (digitsBeforeCaret <= 2) return digitsBeforeCaret;
    if (digitsBeforeCaret <= 4) return digitsBeforeCaret + 1;
    return digitsBeforeCaret + 2;
  };

  const handleInput = (event, val = null) => {
    // to protect against performing onChange operations via devtools manipulation, when field is disabled
    //  from fieldValidation or review tool.
    if (getReviewDisabled(fieldId)) return;
    // Mark as user-edited to remove prefill style
    if (event && prefillStyleConfig) setIsUserEdited(true);
    if (comments && comments[fieldId]) {
      setIsValid(false);
      delete comments[fieldId];
      setComments({ ...comments });
    }
    const shouldAutoFormatDate = Boolean(
      fieldValidation?.isDate && fieldValidation?.autoFormatDateInput
    );
    const rawInputVal = event ? event.target.value : val;
    const isCaretAtEnd = event
      ? (event.target.selectionStart || 0) === rawInputVal.length
      : true;
    const shouldApplyAutoFormat = shouldAutoFormatDate && isCaretAtEnd;
    const inputVal = shouldApplyAutoFormat ? autoFormatDateInput(rawInputVal) : rawInputVal;
    if (event && shouldApplyAutoFormat) {
      const caretPosition = getDateCaretPosition(
        rawInputVal,
        event.target.selectionStart || 0
      );
      event.target.value = inputVal;
      const boundedCaretPosition = Math.min(caretPosition, inputVal.length);
      setTimeout(() => {
        if (document.activeElement === event.target) {
          event.target.setSelectionRange(boundedCaretPosition, boundedCaretPosition);
        }
      }, 0);
    }

    if (event) {
      setIsValid(true);
    } else {
      const matchedEntity = entityComments.find(
        (obj) => obj.compositeKey === entityCompositeKey
          && (!showId || !obj.showId || obj.showId === showId)
      );
      if (matchedEntity && fieldId in matchedEntity) {
        setIsValid(false);
      }
    }

    if (validations.length > 0) {
      setData(inputVal);
      performCustomValidations({ inputVal, validationsToPerform: validations });
    } else {
      setData(inputVal);
      if (!inputVal) setInvalidError(false);
    }

    if (isVerifyCard) {
      if (fieldId === 'cardNumber') {
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null,
            cardNumber: event.target.value,
            cardNameVerify: 1,
            cardMatchStatus: 1,
            caseStatusMatch: 1,
          },
        });
      }
      if (fieldId === 'caseNumber') {
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null,
            caseNumber: event.target.value,
            cardNameVerify: 1,
            cardMatchStatus: 1,
            caseStatusMatch: 1,
          },
        });
      }
    }

    setCurPageValues({ ...curPageValues, [fieldId]: inputVal });
    if (isChangePageValue) {
      changePageValues[fieldId] = inputVal;
      setChangePageValues({ ...changePageValues });
    }
  };

  useEffect(() => {
    if (filledFormData && (filledFormData[fieldId] || filledFormData[fieldId] === '')) {
      setData(filledFormData[fieldId]);
      curPageValues[fieldId] = filledFormData[fieldId];
      setCurPageValues({ ...curPageValues });
      if (isChangePageValue) {
        changePageValues[fieldId] = payloadFieldValue;
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [fieldId, payloadFieldValue]);

  useEffect(() => {
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      let preFilledVal = '';
      if (getVal(filledFormData, selectKey) === 0) {
        preFilledVal = 0;
      } else if (getVal(filledFormData, selectKey)) {
        preFilledVal = getVal(filledFormData, selectKey);
      }
      setData(preFilledVal);
    } else if (isPreFilled && preFilledWith === 'memberInfo' && !fetchUrl) {
      if (memberInfo) {
        // we have to combine the data from memberInfo for below keys
        const preFilledVal = getRequiredData({
          isPreFilled, preFilledWith, fieldValue, selectKey, fetchUrl, filledFormData, memberInfo,
        });
        setData(preFilledVal);
      }
    } else if (fieldValue !== 'default' && fieldValue !== '') {
      setData(fieldValue);
      setCurPageValues({ ...curPageValues, [fieldId]: fieldValue });
    } else if (fieldValidation?.requiredFields?.length > 0) {
      const processedFields = fieldValidation.requiredFields.map((field) => {
        let dynamicResult = 'false';
        if (field.dynamicOperation) {
          dynamicResult = extractMapResponseValueByKey(field, 'dynamicOperation');
        }
        return { ...field, dynamicResult };
      });
      const preFilledVal = calculatorDlsInput({
        requiredFields: processedFields,
        filledFormData,
      });
      setData(preFilledVal);
    } else if (isChangePageValue && fieldId in changePageValues) {
      setData(getVal(changePageValues, fieldId));
    }
  }, isSelectivePrefill ? [preFilledWith === 'filledFormData'
    ? filledFormData[`${selectKey}`] : getVal(dataObject, preFilledWith)[`${selectKey}`]]
    : [memberInfo, filledFormData, fieldId]);

  // Isolated entity prefill section with entity Data. This is scoped to explicit entity sources only

  useEffect(() => {
    if (!isPreFilled || fetchUrl) {
      return;
    }

    if (preFilledWith === 'entityData') {
      const sourceObj = getVal(dataObject, preFilledWith);
      const preFilledVal = getVal(sourceObj, selectKey);
      if (preFilledVal === 0 || preFilledVal) {
        setData(preFilledVal);
      }
    }
  }, [isPreFilled, preFilledWith, fetchUrl, selectKey, dataObject]);

  useEffect(() => {
    setIsValid(true);
    if (entityToEdit && typeof entityToEdit[fieldId] !== 'undefined') {
      handleInput(null, entityToEdit[fieldId]);
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (comments && comments[fieldId]) {
      setIsValid(false);
    }
  }, [comments]);

  useEffect(() => {
    if (isPreFilled && preFilledWith === 'curPageValues' && !fetchUrl) {
      if (curPageValues && getVal(curPageValues, selectKey) !== data) {
        setData(getVal(curPageValues, selectKey));
      }
    }
  }, [curPageValues]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setData('');
      changePageValues[fieldId] = '';
      setChangePageValues({ ...changePageValues });
      delete idsToClear.ids[fieldId];
      dispatchIdsState({
        toggleState: idsToClear.toggleState,
        ids: idsToClear,
      });
      if (!validationErrState.isValid) {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
  }, [idsToClear.toggleState]);

  useEffect(() => {
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: `${fieldId}` },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (isChangePageValue && fieldId in changePageValues) {
      setData(getVal(changePageValues, fieldId));
    }
    if (!validationErrState.isValid) {
      performCustomValidations({ inputVal: getVal(changePageValues, fieldId), validationsToPerform: validations });
    }
  }, [changePageValues, isChangePageValue]);

  const fieldValueValidation = (event, value) => {
    if (fieldValidation.isNumeric) {
      if (!/^\d+$/.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphabet) {
      if (!/^[A-Za-z]+$/.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphaNumeric) {
      const alphaNu = /^[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}]*$/;
      if (alphaNu.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.allowedKeysRegex) {
      const rex = new RegExp(fieldValidation.allowedKeysRegex);
      if (!rex.test(value)) {
        event.preventDefault();
      }
    }
  };

  const handlePaste = (event) => {
    fieldValueValidation(event, event.clipboardData.getData('text'));
  };

  const handleKeyPress = (event) => {
    fieldValueValidation(event, event.key);
  };

  const handleValidation = (e) => {
    const str = e.target.value;
    /* eslint-disable-next-line no-useless-escape -- required */
    const re = /^[\d/a-z\-]+$/i;
    if (!re.test(str)) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.minSize) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.maxSize) {
      setInvalidError(true);
    } else {
      setInvalidError(false);
    }

    if (fieldValidation.isNumberFormatRequired) {
      const formattedValue = (
        Number(str.replace(/\D/g, '')) || ''
      ).toLocaleString();
      setData(formattedValue);
    }

    if (fieldValidation.isSingleToDoubleByteConversion) {
      setData(str.replace(/ /g, ''));
    }
  };

  const invalidHandler = (e) => {
    if (validations.length > 0 || dynamicValidations.length > 0) {
      e.preventDefault();
      const validationsToPerform = validations.length > 0 ? validations : dynamicValidations;
      performCustomValidations({ inputVal: null, validationsToPerform });
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`)?.focus();
      if (!e.target.value) {
        setCustomInvalidMsg('Please complete this field');
      } else {
        setCustomInvalidMsg(
          fieldValidation.customInvalidMsg || 'Please fill in this field'
        );
      }
      setShowCustomInvalidMsg(true);
      setStatus('error');
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
  };

  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
    setStatus('default');
  };

  const handleOnBlur = (e) => {
    if (operationsToPerform?.onBlur?.length > 0) {
      performOperations({
        value: e.target.value,
        operations: operationsToPerform.onBlur,
        dataObject: {
          memberInfo,
          formSubmissionPayload,
          curPageValues,
          journeyId,
          iformIntegrationUrls,
        },
        functionObject: { setFormSubmissionPayload, loggerApiCall },
        setFormSubmissionPayload,
        isValid: validationErrState.isValid,
      });
    }
    if (e?.target?.value) {
      setData(() => e.target.value.trim());
    }
    if (inputFormat && e.target.value) {
      setData((prev) => formatUnFormatString(prev, inputFormat, true));
    }
  };

  const handleOnKeyDown = (e) => {
    // Handle Enter key (keyCode 13 or e.key === 'Enter')
    if (e.keyCode === 13 || e.key === 'Enter') {
      e.preventDefault(); // Prevent default form submission
    }
    if (operationsToPerform?.onKeyDown?.length > 0) {
      // The native form submit (FormData) is what normally syncs this field's typed value
      // into formSubmissionPayload. Since we bypass that submit here, mirror the current
      // input value in ourselves so operations (e.g. callApi resourceId lookups) see it.
      const mergedDataObject = {
        ...dataObject,
        iformIntegrationUrls,
      };
      if (mergedDataObject.formSubmissionPayload) {
        mergedDataObject.formSubmissionPayload[fieldId] = e.target.value;
      }
      setFormSubmissionPayload((prev) => ({ ...prev, [fieldId]: e.target.value }));
      performOperations({
        value: e.target.value,
        operations: operationsToPerform.onKeyDown,
        dataObject: mergedDataObject,
        functionObject: { setFormSubmissionPayload, loggerApiCall, setIsFormSubmission },
        setFormSubmissionPayload,
        isValid: validationErrState.isValid,
      });
    }
  };

  const handleOnFocus = (e) => {
    if (inputFormat && e.target.value) {
      setData(formatUnFormatString(e.target.value, inputFormat, false));
    }
  };

  const showErrorMessage = (errorMessage) => (
    <ComponentLevelNotification
      id={`${fieldId}-errorMessage`}
      className="pad-1-t"
    >
      {parseHtml(errorMessage)}
    </ComponentLevelNotification>
  );

  const inputLabel = fieldLabel && fieldLabel.label && (
    <Label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
      {parseHtml(fieldLabel.label)}
      {!fieldValidation.required && optionalLabel && (
        <span className={fieldLabel.cssProperties.style}>{optionalLabel}</span>
      )}
    </Label>
  );

  const toolTip = fieldValidation && fieldValidation.isToolTip && (
    <>
      <span> </span>
      <Tooltip id={`${fieldId}-tooltip`}>
        <TooltipTrigger>
          <InfoTooltipButton screenReaderLabel="info" />
        </TooltipTrigger>
        <TooltipContent
          style={fieldValidation.toolTipStyles || {}}
        >
          {parseHtml(fieldValidation.toolTipInfo)}
        </TooltipContent>
      </Tooltip>
    </>
  );

  const hint = fieldLabel && fieldLabel.subLabel && (
    <div>
      <Label
        className={fieldLabel.cssProperties.subLabelStyle}
        htmlFor={fieldId}
      >
        {parseHtml(fieldLabel.subLabel)}
      </Label>
    </div>
  );
  const prefillInlineStyle = showPrefillStyle ? prefillStyleConfig.style || {} : {};
  const renderElement = (
    <Input
      status={status}
      // This isValid is for showing red borders for the comments from the review tool.
      // No input field should show comments if it does not have any data.(Entity page)
      style={{
        border: !isValid && data ? '.0625rem solid red' : 'none',
        ...prefillInlineStyle,
      }}
      className={showPrefillStyle ? prefillStyleConfig.className || '' : ''}
      label={inputLabel}
      tooltip={toolTip}
      hint={hint}
      type={
        fieldValidation && fieldValidation.type
          ? fieldValidation.type
          : 'text'
      }
      id={fieldId}
      key={fieldId}
      name={fieldId}
      pattern={
        fieldValidation && fieldValidation.pattern
          ? fieldValidation.pattern
          : '*'
      }
      value={data && textWidth ? convertFullToHalfWidth(data) : data}
      placeholder={fieldPlaceholder}
      onChange={handleInput}
      aria-disabled={fieldValidation.disabled || getReviewDisabled(fieldId)}
      required={fieldValidation.required}
      maxLength={fieldValidation.maxSize}
      minLength={fieldValidation.minSize}
      onKeyPress={handleKeyPress}
      onKeyDown={handleOnKeyDown}
      onPaste={handlePaste}
      onKeyUp={handleValidation}
      title=""
      onInvalid={invalidHandler}
      onInput={onInputHandler}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
    />
  );

  return (
    <>
      {renderElement}
      {fieldValidation.disabled && (
        <input type="hidden" id={`${fieldId}-hidden`} name={fieldId} value={data} />
      )}
      {inValidError && fieldValidation?.error
        && showErrorMessage(fieldValidation.error)}
      {showCustomInvalidMsg
        && showErrorMessage(customInvalidMsg)}
      {validationErrState.validationErr
        && showErrorMessage(validationErrState.validationErr)}
      {comments && comments[fieldId]
        && showErrorMessage(comments[fieldId].comment)}
      {(() => {
        const matchedEntity = entityComments.find(
          (obj) => obj.compositeKey === entityCompositeKey
            && (!showId || !obj.showId || obj.showId === showId)
        );
        return matchedEntity && fieldId in matchedEntity && !isValid
          && showErrorMessage(
            fieldId === 'compositeKey'
              ? matchedEntity[fieldId]
              : matchedEntity[fieldId].defaultValue
          );
      })()}
    </>
  );
};
/* eslint-enable max-len -- need */
DlsInput.propTypes = {
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValue: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  preFilledWith: PropTypes.string,
  fetchUrl: PropTypes.string,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.bool,
  textWidth: PropTypes.string,
  isVerifyCard: PropTypes.bool,
  setFormSubmissionPayload: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityComments: PropTypes.objectOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  showId: PropTypes.string,
  dynamicValidations: PropTypes.arrayOf(PropTypes.shape),
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  dispatchIdsState: PropTypes.func,
  dynamicValidationDeps: PropTypes.arrayOf(PropTypes.string),
  extractMapResponseValueByKey: PropTypes.func,
  dataObject: PropTypes.objectOf(PropTypes.shape),
  inputFormat: PropTypes.string,
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  isChangePageValue: PropTypes.bool,
  fieldDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  journeyId: PropTypes.string,
  iformIntegrationUrls: PropTypes.string,
  loggerApiCall: PropTypes.func,
  isSelectivePrefill: PropTypes.bool,
  getReviewDisabled: PropTypes.func,
  objectOfFunctions: PropTypes.objectOf(PropTypes.shape),
  prefillStyleConfig: PropTypes.shape({
    sourcePath: PropTypes.string,
    matchAPIKey: PropTypes.string,
    style: PropTypes.objectOf(PropTypes.string),
    className: PropTypes.string,
  }),
  setIsFormSubmission: PropTypes.func,
};

export default DlsInput;
